// Wing Dashboard — Steptember MVP: step-focus (Feb 2026 rewrite).
// See /app/memory/PRD.md "Latest Change Log" for the strict-steps spec.
// All non-step blocks (activity rings, AI Coach brief, metric tiles,
// momentum hero, path of wyngx, weekly report, fiber satiety, etc.) are
// intentionally NOT rendered here — the imports/components stay in the
// repo so post-MVP re-enable is a one-liner in the render path.
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/api";
import { useAuth } from "@/auth/AuthContext";
import LanguagePicker from "@/components/LanguagePicker";
import LiveSyncDot from "@/components/LiveSyncDot";
import PullToRefresh from "@/components/PullToRefresh";
import TodayInPlan from "@/components/dashboard/TodayInPlan";
import DashboardActionCards from "@/components/dashboard/DashboardActionCards";
import WhoChallengedYou from "@/components/dashboard/WhoChallengedYou";
import StepsHeroCard from "@/components/dashboard/StepsHeroCard";
// CrewPulse-import verwijderd (Feb 2026): kaart is uit het dashboard —
// invite-flow leeft in de challenge empty-state en de matchmake-sheet.
import { setCache } from "@/lib/pageCache";
export default function Dashboard() {
  const {
    t
  } = useTranslation();
  const {
    i18n
  } = useTranslation();
  const {
    user
  } = useAuth();

  // Spotlight FIRST_LOGIN tour is disabled for the Steptember MVP —
  // its target ids (momentum-hero, wheel-handle, recovery, tile-hrv,
  // …) all point at blocks we removed from the render path.  When we
  // ship a step-focused onboarding tour it can be re-enabled here.

  // Ensure user's saved language wins on first render + warm the day
  // cache so downstream components mount instantly on repeat visits.
  useEffect(() => {
    (async () => {
      try {
        const {
          data
        } = await api.get("/settings/me");
        if (data?.language && data.language !== i18n.language) {
          i18n.changeLanguage(data.language);
        }
        const todayStr = new Date().toISOString().slice(0, 10);
        const {
          data: today
        } = await api.get(`/health/day?date=${todayStr}`);
        setCache("dashboard:day:today", today);
      } catch {/* silent — components fetch their own data */}
    })();
  }, []);

  // ────────────────────────────────────────────────────────────────
  // MVP DASHBOARD — Steptember step-focus.
  //
  // Fixed order (matches spec + mock):
  //   1. Header:  greeting + subtitle + LiveSyncDot (top-left) + Lang picker (right)
  //   2. StepsHeroCard:  today's step count + ring + friend ranking
  //   3. ActiveChallengeCTA:  summary card → nav to /challenges (Feb 2026 redesign)
  //   4. TodayInPlan:  next meet-up / challenge / event
  //   5. CrewPulse:  who's playing with you today
  //   6. WhoChallengedYou:  viral loop (auto-hides when empty)
  //   7. QuickActions:  3 buttons (Challenge · Meet-up · Chats)
  //
  // All other blocks (AI Coach Brief, activity rings, momentum hero,
  // metric tiles, weekly report, save streak, achievements, fiber
  // satiety, shadow battle, path of wyngx, hall of heroes, crew map,
  // focus widget, territory, week strip, top-bar badges/wings/theme
  // toggle) are HIDDEN by removing them from the render path — the
  // component code stays in the repo so post-MVP re-enable is a
  // one-liner.
  // ────────────────────────────────────────────────────────────────
  const firstName = (user?.name || "Athlete").split(" ")[0];
  return <PullToRefresh>
        <div className="app-shell" data-testid="dashboard-page">
            <header className="px-5 pt-12 pb-2 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2 h-4">
                        <LiveSyncDot />
                    </div>
                    <h1 className="font-display text-[32px] sm:text-[36px] font-semibold tracking-tighter leading-tight" data-testid="dashboard-greeting">
                        {t("dashboard.hi")} {firstName} <span aria-hidden="true">👋</span>
                    </h1>
                    <p className="text-[13px] text-muted-foreground mt-1">
                        {t("dashboard.let_s_get_those_steps_in_today")}
                    </p>
                </div>
                <div className="shrink-0 pt-1">
                    <LanguagePicker persistRemote />
                </div>
            </header>

            {/* 1. Steps Hero — big step count + ring + friend rank */}
            <StepsHeroCard />

            {/* 2. Vandaag in Plan — next upcoming meetup/challenge/event.
                 Verbergt zichzelf wanneer er niets is gepland. */}
            <div data-testid="block-fixed-today-plan">
                <TodayInPlan />
            </div>

            {/* Kaart CREW (Invite friends) is verwijderd (Feb 2026) — de
                friends-invite flow leeft al in de challenge empty-state,
                de matchmake-sheet én in Social. Op het dashboard was 'ie
                redundant. */}

            {/* 3. Wie daagde je uit? — viral loop (auto-hides when empty) */}
            <WhoChallengedYou />

            {/* 4. Dashboard action-cards (Feb 2026 redesign) — 3 grote
                 horizontale kaarten (Challenges · Meet-up · Chats) met
                 live counts. Vervangt zowel de gele ActiveChallengeCTA-
                 samenvattingsbalk als de kleine 3-koloms QuickActions
                 tegels — minder ruis, groter tap-target, duidelijker CTA. */}
            <div data-testid="block-fixed-action-cards">
                <DashboardActionCards />
            </div>

            {/* Bottom spacer so the floating bottom-nav doesn't cover the
                last block. */}
            <div className="h-32" />
        </div>
        </PullToRefresh>;
}